import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import type { PortalMembership } from '../api/portalSession';
import { PropertyContext } from './propertyContextDef';

const STORAGE_KEY_PREFIX = 'namibiago-portal:selected-business-id';

/** Namespaced by user id (fix round 1, item 1) — two accounts signed into
 * the same browser over time must never leak each other's last-selected
 * property, and this is the direct fix rather than relying solely on
 * AuthProvider's query-cache clear on sign-out/account-switch (which
 * doesn't touch localStorage at all). */
function storageKey(userId: string): string {
  return `${STORAGE_KEY_PREFIX}:${userId}`;
}

/** localStorage is wrapped in try/catch (plan.md brief: "the selected
 * property persists per viewer (localStorage wrapped in try/catch is fine
 * for the selection only)") — a private window or blocked site data must
 * never crash the switcher, just fall back to "pick again this session". */
function readStoredBusinessId(userId: string): string | null {
  try {
    return window.localStorage.getItem(storageKey(userId));
  } catch {
    return null;
  }
}

function writeStoredBusinessId(userId: string, id: string | null): void {
  try {
    if (id) window.localStorage.setItem(storageKey(userId), id);
    else window.localStorage.removeItem(storageKey(userId));
  } catch {
    /* quota/denied — selection just won't survive a reload */
  }
}

/** Multi-property from day one (plan L346-348): every query key, Realtime
 * channel and persisted cache entry downstream must be scoped by the
 * `businessId` this context exposes.
 *
 * `loading` gates when the stored selection is trusted at all (fix round 1,
 * item 1): while `usePortalSession()` is still resolving, `businessId`
 * stays `null` rather than optimistically exposing whatever id happens to
 * be in `localStorage` — that id could belong to a business the viewer no
 * longer has (or never had) access to, and briefly rendering as though it
 * were selected (before the membership list arrives to contradict it) is
 * exactly the kind of state a booking/settlement screen must never trust.
 * Once `loading` clears, the stored id is used only if it's a live
 * membership; otherwise the first membership, or `null` if there are none. */
export function PropertyProvider({
  memberships,
  loading,
  userId,
  children,
}: {
  memberships: PortalMembership[];
  loading: boolean;
  userId: string;
  children: ReactNode;
}) {
  const [businessId, setBusinessId] = useState<string | null>(null);

  useEffect(() => {
    if (loading) {
      setBusinessId(null);
      return;
    }
    const stored = readStoredBusinessId(userId);
    const storedIsLive = stored !== null && memberships.some((m) => m.businessId === stored);
    const resolved = storedIsLive ? stored : (memberships[0]?.businessId ?? null);
    setBusinessId(resolved);
    if (resolved !== stored) writeStoredBusinessId(userId, resolved);
  }, [loading, memberships, userId]);

  const selectBusiness = useCallback(
    (id: string) => {
      setBusinessId(id);
      writeStoredBusinessId(userId, id);
    },
    [userId],
  );

  const role = useMemo(
    () => memberships.find((m) => m.businessId === businessId)?.role ?? null,
    [memberships, businessId],
  );

  const value = useMemo(
    () => ({ businessId, role, memberships, selectBusiness }),
    [businessId, role, memberships, selectBusiness],
  );

  return <PropertyContext.Provider value={value}>{children}</PropertyContext.Provider>;
}
